export const KEYWORD_MATCH_TYPE_OPTIONS = [
  { id: "broad", label: ($) => $.programs.matchType["Broad"], value: "broad" },
  { id: "exact", label: ($) => $.programs.matchType["Exact"], value: "exact" },
  { id: "phrase", label: ($) => $.programs.matchType["Phrase"], value: "phrase" },
];

export const TARGET_MATCH_TYPE_OPTIONS = [
  { id: "auto", label: ($) => $.programs.matchType["Auto"], value: "auto" },
  { id: "product", label: ($) => $.programs.matchType["Product"], value: "product" },
  { id: "category", label: ($) => $.programs.matchType["Category"], value: "category" },
  { id: "dynamic", label: ($) => $.programs.matchType["Dynamic"], value: "dynamic" },
  {
    id: "expression",
    label: ($) => $.programs.matchType["Expression"],
    value: "expression",
  },
];

export const DEFAULT_CONDITIONS = {
  pricing: {
    option: JSON.stringify([">", "${units}"]),
    values: [10],
  },
  bidding: {
    option: JSON.stringify([">", "${conversions}"]),
    values: [10],
    scope: {
      values: { days: "7" },
    },
  },
  budget: {
    option: JSON.stringify(["<", "${acos}"]),

    values: ["${max_acos}"],
  },
  lineitem_bidding: {
    option: JSON.stringify([">", "${tacos}"]),
    values: [25],
    scope: {
      values: { days: "14" },
    },
  },
  harvesting: {
    option: JSON.stringify([">", "${conversions}"]),
    values: [3],
    scope: {
      values: { days: "7" },
    },
  },
  harvesting_match: {
    option: JSON.stringify(["contains", "${query}"]),
    values: [""],
  },
  tagging: {
    option: JSON.stringify([">", "${acos}"]),
    values: [30],
    scope: {
      values: { days: "7" },
    },
  },
};

// The rules engine applies any SET_* amount as is, whatever the operator, so these take an amount in currency.
export const FIXED_AMOUNT_ACTIONS = ["SET_BID", "SET_BUDGET", "SET_BASE_BID"];

export const DEFAULT_ACTIONS = {
  pricing: {
    function: "INCREASE_PRICE",
    operator: "adjust_fixed",
    values: [10],
  },
  bidding: {
    function: "INCREASE_BID",
    operator: "adjust_fixed",
    values: [0.05],
  },
  budget: {
    function: "INCREASE_BUDGET",
    operator: "adjust_percent",
    values: [10],
  },
  lineitem_bidding: {
    function: "INCREASE_BASE_BID",
    operator: "adjust_fixed",
    values: [0.05],
  },
  harvesting: {
    function: "HARVEST_QUERY",
    operator: "harvest",
    values: [],
  },
  tagging: {
    function: "ADD_TAG",
    operator: "apply",
    values: [],
  },
};

const ALGO_FALLBACK_RULE = {
  scope: {
    values: { days: "7" },
    operator: "after",
  },
  actions: [
    {
      function: "USE_ALGO",
      values: [],
      operator: "",
    },
  ],
  condition: "all",
  conditions: [],
  comment: "Use algorithm if no other rules match",
};

export const RULE_DEFAULTS = {
  // The conditions that get pushed when appending are different from the defaults for pricing
  // Verify which one is the source of truth
  pricing: {
    scope: {
      operator: "after",
      values: { days: "7" },
    },
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["pricing"]],
    actions: [
      {
        function: "INCREASE_PRICE",
        operator: "adjust_fixed",
        values: [10],
      },
    ],
  },
  bidding: {
    scope: {
      values: {
        days: "7",
      },
      operator: "after",
    },
    actions: [
      {
        function: "INCREASE_BID",
        values: [0.15],
        operator: "adjust_fixed",
      },
    ],
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["bidding"]],
  },
  budget: {
    scope: {
      values: {
        days: "7",
      },
      operator: "after",
    },
    actions: [
      {
        function: "INCREASE_BUDGET",
        values: [10],
        operator: "adjust_percent",
      },
    ],
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["budget"]],
  },
  lineitem_bidding: {
    scope: {
      values: {
        days: "7",
      },
      operator: "after",
    },
    actions: [
      {
        function: "INCREASE_BASE_BID",
        values: [0.15],
        operator: "adjust_fixed",
      },
    ],
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["lineitem_bidding"]],
  },
  harvesting: {
    actions: [
      {
        function: "HARVEST_QUERY",
        values: [],
        operator: "harvest",
      },
    ],
    match: "true",
    matches: [],
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["harvesting"]],
  },
  tagging: {
    actions: [
      {
        function: "ADD_TAG",
        operator: "apply",
        values: [],
      },
    ],
    condition: "all",
    conditions: [DEFAULT_CONDITIONS["tagging"]],
  },
};

export const PROGRAM_DEFAULTS = {
  pricing: {
    rules: [
      {
        scope: {
          operator: "before",
          values: [7],
        },
        condition: "all",
        conditions: [DEFAULT_CONDITIONS["pricing"]],
        actions: [
          {
            function: "INCREASE_PRICE",
            values: { how: "adjust_percent", amount: 10 },
          },
        ],
      },
    ],
  },
  bidding: {
    name: null,
    start_date: null,
    end_date: null,
    rules: [RULE_DEFAULTS["bidding"], ALGO_FALLBACK_RULE],
    initial_action: {
      function: "SET_BID",
      values: [0.85],
      operator: "adjust_fixed",
    },
  },
  budget: {
    name: null,
    start_date: null,
    end_date: null,
    rules: [RULE_DEFAULTS["budget"]],
  },
  lineitem_bidding: {
    name: null,
    start_date: null,
    end_date: null,
    initial_action: {
      function: "SET_BASE_BID",
      values: [0.85],
      operator: "adjust_fixed",
    },
    rules: [RULE_DEFAULTS["lineitem_bidding"]],
  },
  harvesting: {
    name: null,
    start_date: null,
    end_date: null,
    rules: [RULE_DEFAULTS["harvesting"]],
  },
  tagging: {
    name: null,
    start_date: null,
    end_date: null,
    is_enabled: true,
    rules: [RULE_DEFAULTS["tagging"]],
  },
};

// Make global set of options
export const OPERATOR_OPTIONS = {
  number: [
    { value: ">", label: ($) => $.programs.operator["greater than"] },
    { value: "<", label: ($) => $.programs.operator["less than"] },
    { value: ">=", label: ($) => $.programs.operator["greater than or equal to"] },
    { value: "<=", label: ($) => $.programs.operator["less than or equal to"] },
    { value: "=", label: ($) => $.programs.operator["equal to"] },
    { value: "between", label: ($) => $.programs.operator["between"] },
  ],
  select: [{ value: "=", label: ($) => $.programs.operator["equal to"] }],
  "multi-select": [{ value: "contains", label: ($) => $.programs.operator["equal to"] }],
  text: [
    { value: "contains", label: ($) => $.programs.operator["contains"] },
    { value: "contains_word", label: ($) => $.programs.operator["contains word"] },
    { value: "starts_with", label: ($) => $.programs.operator["starts with"] },
    { value: "ends_with", label: ($) => $.programs.operator["ends with"] },
    { value: "=", label: ($) => $.programs.operator["equals"] },
    { value: "not contain", label: ($) => $.programs.operator["does not contain"] },
    { value: "not contain_word", label: ($) => $.programs.operator["does not contain word"] },
  ],
};

export const NON_METRIC_FIELD_ACCESSORS = ["target_match_types", "keyword_match_types", "tags_enhanced"];

export const RELATIVE_METRIC_FIELD_ACCESSORS = {
  bidding: [
    {
      accessor: "acos",
      options: [
        { value: "${target_acos}", label: ($) => $.programs.relativeMetric["Optimal ACOS Range Low"] },
        { value: "${max_acos}", label: ($) => $.programs.relativeMetric["Optimal ACOS Range High"] },
      ],
    },
    {
      accessor: "roas",
      options: [
        { value: "${target_roas}", label: ($) => $.programs.relativeMetric["Optimal ROAS Range High"] },
        { value: "${min_roas}", label: ($) => $.programs.relativeMetric["Optimal ROAS Range Low"] },
      ],
    },
    {
      accessor: "cpc",
      options: [{ value: "${bid}", label: ($) => $.programs.relativeMetric["Current Bid"] }],
    },
  ],
  budget: [
    {
      accessor: "acos",
      options: [
        { value: "${target_acos}", label: ($) => $.programs.relativeMetric["Optimal ACOS Range Low"] },
        { value: "${max_acos}", label: ($) => $.programs.relativeMetric["Optimal ACOS Range High"] },
      ],
    },
    {
      accessor: "roas",
      options: [
        { value: "${target_roas}", label: ($) => $.programs.relativeMetric["Optimal ROAS Range High"] },
        { value: "${min_roas}", label: ($) => $.programs.relativeMetric["Optimal ROAS Range Low"] },
      ],
    },
  ],
};
